@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.enginepalettenkonto

import org.example.project.dashboard.AppDialogs

import androidx.compose.runtime.*
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import org.example.project.dashboard.LocalScreenRefreshVersion
import org.example.project.enginekartei.KarteiApi
import org.example.project.engineletter.LETTER_LAYOUT_PALETTENKONTOABSCHLUSS
import org.example.project.engineletter.layouts.PalettenkontoabschlussLayout
import org.example.project.engineletter.renderLetterDocumentPdfFile
import org.example.project.engineservice.ServiceApi
import org.example.project.enginetabular.TABULAR_DOCUMENT_ICON_VALUE
import org.example.project.enginetabular.TabularScreen
import org.example.project.enginetabular.TabularSplitScreen
import org.example.project.enginetabular.TabularToolbarButton
import org.example.project.service.GlobalManager
import org.example.project.service.LoginService
import org.example.project.service.ServiceDocumentViewerService
import org.example.project.service.YardSettingsService
import org.example.project.service.ZustandManager
import org.example.project.utils.All
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.left
import org.jetbrains.compose.web.css.position
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.top
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.HTMLElement

private val partnerHeaders = listOf(
    "Firma", "Straße", "Hausnummer", "PLZ", "Ort", "Tauschart", "Kontostand",
    "Ansprechpartner", "Email", "Telefon", "Mobil", "Abschlusstag"
)

private val kontoHeaders = listOf("Palettentyp", "Eingang", "Ausgang", "Kontostand")

private val auskunftHeaders = listOf(
    "Datum", "Tauschpartner", "Lieferscheinnummer", "Auftraggeber", "Empfänger", "Unternehmer", "Lieferant", "Palettentyp", "Vorgangstyp",
    "Eingang", "Ausgang", "Differenz", "Kontostand", "LKW Kennzeichen", "Auflieger Kennzeichen", "LKW Fahrer",
    "Palettenschein", "Auftragsdokumentationen", "QM Dokumentation", "Lieferscheine",
    "Pal Soll", "Pal Ist", "Buchungsgrund", "Zustand", "Gebucht von", "Gebucht Am", "Gebucht Um"
)

private val journalHeaders = listOf(
    "Datum", "Tauschpartner", "Lieferscheinnummer", "Auftraggeber", "Empfänger", "Unternehmer", "Lieferant", "Palettentyp", "Vorgangstyp",
    "Eingang", "Ausgang", "Buchungsgrund", "Zustand"
)

private val differenzHeaders = listOf(
    "Datum", "Tauschpartner", "Lieferscheinnummer", "Auftraggeber", "Empfänger", "Unternehmer", "Lieferant", "Palettentyp", "Vorgangstyp",
    "Eingang", "Ausgang", "Pal Soll", "Pal Ist", "Buchungsgrund", "Zustand"
)

private fun currentPalMonthKey(): String {
    val now = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault())
    return "${now.year}_${now.monthNumber.toString().padStart(2, '0')}"
}

private fun currentPalMonthLabel(): String {
    val now = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault())
    return "${palMonthName(now.monthNumber)} ${now.year}"
}

private fun currentPalDate(): String =
    Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date.toString()

private fun currentPalTime(): String {
    val time = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).time
    return "${time.hour.toString().padStart(2, '0')}:${time.minute.toString().padStart(2, '0')}"
}

private fun currentFirmenId(): String =
    LoginService.getUser()?.firmenId.orEmpty().trim().ifBlank { GlobalManager.currentFirmenId.trim() }

private fun currentUserLabel(): String =
    LoginService.getKuerzel().ifBlank { LoginService.getId() }

@Composable
fun PalVerarbeitungScreen() {
    PalVerarbeitungVerwaltungScreen()
}

@Composable
private fun PalVerarbeitungVerwaltungScreen() {
    val scope = rememberCoroutineScope()
    var partner by remember { mutableStateOf<List<All>>(emptyList()) }
    var kontoAlle by remember { mutableStateOf<List<All>>(emptyList()) }
    var kontoDetail by remember { mutableStateOf<List<All>>(emptyList()) }
    var selectedPartnerId by remember { mutableStateOf("") }
    var selectedKontoRowId by remember { mutableStateOf("") }
    var abschlussPdf by remember { mutableStateOf<All?>(null) }
    var abschlussUnternehmen by remember { mutableStateOf(All()) }
    var abschlussLogoSrc by remember { mutableStateOf("") }
    var partnerFilter by remember { mutableStateOf<Map<String, String>?>(null) }
    var kontoFilter by remember { mutableStateOf<Map<String, String>?>(null) }
    val refreshVersion = LocalScreenRefreshVersion.current

    suspend fun reload() {
        partner = partnerFilter?.let { PalettenkontoApi.searchDashboard("verarbeitung-partner", it) }.orEmpty()
        kontoAlle = emptyList()
        kontoDetail = kontoFilter?.let {
            PalettenkontoApi.searchDashboard("konto-detail", it, mapOf("partnerId" to selectedPartnerId))
        }.orEmpty()
        if (selectedPartnerId.isBlank() && partner.isNotEmpty()) selectedPartnerId = partner.first().id
    }

    LaunchedEffect(refreshVersion) { reload() }
    LaunchedEffect(selectedPartnerId) {
        if (selectedPartnerId.isNotBlank()) {
            kontoDetail = kontoFilter?.let {
                PalettenkontoApi.searchDashboard("konto-detail", it, mapOf("partnerId" to selectedPartnerId))
            }.orEmpty()
            selectedKontoRowId = ""
        }
    }

    LaunchedEffect(abschlussPdf?.id) {
        val exportAbschluss = abschlussPdf ?: return@LaunchedEffect
        val root = document.getElementById("palettenkontoabschluss-export-root") as? HTMLElement ?: return@LaunchedEffect
        val fileName = palettenkontoabschlussPdfFileName(exportAbschluss)
        val pdfFile = renderLetterDocumentPdfFile(root, fileName)
        val pdfUrl = window.asDynamic().URL.createObjectURL(pdfFile) as String
        ServiceDocumentViewerService.show(fileName, pdfUrl)
        abschlussPdf = null
    }

    val kontoSumByPartner = kontoAlle
        .groupBy { palExtra(it, "kundenId").ifBlank { it.kunde } }
        .mapValues { (_, values) -> values.sumOf { it.palettenanzahl } }

    TabularSplitScreen(
        screen1 = {
            TabularScreen(
                screenKey = "PALVerarbeitung",
                rollenKey = "PALVerarbeitung",
                title = "Palettenkonto Übersicht",
                headers = partnerHeaders,
                models = partner,
                data = partner.map { row -> row.id to partnerRow(row, kontoSumByPartner[row.id] ?: row.palettenanzahl) },
                editable = true,
                nonEditableCols = setOf("Firma", "Straße", "Hausnummer", "PLZ", "Ort", "Kontostand"),
                dropdownOptions = mapOf("Tauschart" to listOf("Kölner", "Bonner")),
                includeMetadata = true,
                allowCreate = false,
                allowDelete = false,
                allowSave = true,
                allowImportButton = false,
                allowRefreshButton = true,
                allowExportButton = true,
                allowSearchButton = true,
                smartMode = false,
                onRowClick = { selectedPartnerId = it },
                onRefresh = { scope.launch { reload() } },
                onSearchWithFilters = { filters ->
                    scope.launch {
                        partnerFilter = filters
                        partner = PalettenkontoApi.searchDashboard("verarbeitung-partner", filters)
                        kontoAlle = emptyList()
                    }
                },
                onCommit = { committed ->
                    scope.launch {
                        val saved = committed.map { (id, cols) ->
                            partnerFromRow(id, cols, partner.firstOrNull { it.id == id })
                        }
                        partner = PalettenkontoApi.savePartner(saved, currentFirmenId())
                        reload()
                    }
                }
            )
        },
        screen2 = {
            TabularScreen(
                screenKey = "PALVerarbeitung",
                rollenKey = "PALVerarbeitung",
                title = "Palettenkonto Kontostand",
                headers = kontoHeaders,
                models = kontoDetail,
                data = kontoDetail.map { row -> row.id to kontoRow(row) },
                editable = false,
                hardReadOnly = true,
                nonEditableCols = kontoHeaders.toSet(),
                includeMetadata = true,
                allowCreate = false,
                allowDelete = false,
                allowSave = false,
                allowImportButton = false,
                allowRefreshButton = false,
                allowExportButton = true,
                allowSearchButton = true,
                smartMode = false,
                onRowClick = { rowId -> selectedKontoRowId = rowId },
                onSearchWithFilters = { filters ->
                    scope.launch {
                        kontoFilter = filters
                        kontoDetail = PalettenkontoApi.searchDashboard(
                            "konto-detail", filters, mapOf("partnerId" to selectedPartnerId)
                        )
                    }
                },
                onCommit = {}
            )
        }
    )

    if (abschlussPdf != null) {
        Div({
            id("palettenkontoabschluss-export-root")
            style {
                position(Position.Fixed)
                left((-10000).px)
                top(0.px)
                width(794.px)
                property("z-index", "-1")
                property("pointer-events", "none")
            }
        }) {
            PalettenkontoabschlussLayout(
                rootElementId = null,
                auftrag = abschlussPdf ?: All(),
                unternehmen = abschlussUnternehmen,
                logoSrc = abschlussLogoSrc
            )
        }
    }
}

@Composable
fun PalAuskunftScreen() {
    val scope = rememberCoroutineScope()
    var rows by remember { mutableStateOf<List<All>>(emptyList()) }
    var documentsByKey by remember { mutableStateOf<Map<String, List<All>>>(emptyMap()) }
    var letzteFilter by remember { mutableStateOf<Map<String, String>?>(null) }
    val refreshVersion = LocalScreenRefreshVersion.current

    suspend fun reload() {
        val loaded = letzteFilter?.let { PalettenkontoApi.searchDashboard("auskunft", it) }.orEmpty()
        rows = loaded
        documentsByKey = palLoadDocuments(loaded)
    }

    LaunchedEffect(refreshVersion) { reload() }

    TabularScreen(
        screenKey = "PALAuskunft",
        rollenKey = "PALAuskunft",
        title = "Palettenkonto Auskunft",
        headers = auskunftHeaders,
        models = rows,
        data = rows.map { row -> row.id to auskunftRow(row, documentsByKey) },
        editable = false,
        hardReadOnly = true,
        includeMetadata = true,
        allowCreate = false,
        allowDelete = false,
        allowSave = false,
        allowImportButton = false,
        allowRefreshButton = true,
        allowExportButton = true,
        allowSearchButton = true,
        smartMode = false,
        onCellClick = { rowId, colIndex ->
            val kind = palAuskunftDocumentKind(colIndex)
            if (kind.isNotBlank()) scope.launch { palOpenDocument(rows, documentsByKey, rowId, kind) }
        },
        onRefresh = { scope.launch { reload() } },
        onSearchWithFilters = { filters ->
            scope.launch {
                letzteFilter = filters
                rows = PalettenkontoApi.searchDashboard("auskunft", filters)
            }
        },
        onCommit = {}
    )
}

@Composable
fun PalJournalScreen() {
    val scope = rememberCoroutineScope()
    var rows by remember { mutableStateOf<List<All>>(emptyList()) }
    var journalAuftraege by remember { mutableStateOf<List<All>>(emptyList()) }
    var letzteFilter by remember { mutableStateOf<Map<String, String>?>(null) }
    val refreshVersion = LocalScreenRefreshVersion.current

    suspend fun reload() {
        rows = letzteFilter?.let { PalettenkontoApi.searchDashboard("journal", it) }.orEmpty()
        journalAuftraege = emptyList()
    }

    LaunchedEffect(refreshVersion) { reload() }

    TabularScreen(
        screenKey = "PALJournal",
        rollenKey = "PALJournal",
        title = "Palettenkonto Journal",
        headers = journalHeaders,
        models = rows,
        data = rows.map { row -> row.id to journalRow(row) },
        editable = true,
        nonEditableCols = setOf("Tauschpartner", "Auftraggeber", "Empfänger", "Unternehmer", "Lieferant", "Palettentyp", "Vorgangstyp", "Zustand"),
        createButtonLabel = "Erstellen",
        createRow = { _ ->
            listOf(currentPalDate(), "", "", "", "", "", "", "", "", "", "", "", "In Klärung")
        },
        includeMetadata = true,
        allowCreate = true,
        allowDelete = false,
        allowSave = true,
        allowImportButton = false,
        allowRefreshButton = true,
        allowExportButton = true,
        allowSearchButton = true,
        smartMode = false,
        onCellEditTransform = { _, colName, _, row ->
            if (colName == "Lieferscheinnummer") journalRowWithAuftrag(row, journalAuftraege) else row
        },
        onRefresh = { scope.launch { reload() } },
        onSearchWithFilters = { filters ->
            scope.launch {
                letzteFilter = filters
                rows = PalettenkontoApi.searchDashboard("journal", filters)
            }
        },
        onCommit = { committed ->
            scope.launch {
                committed.forEach { (id, cols) ->
                    val existing = rows.firstOrNull { it.id == id }
                    val lieferscheinnummer = cols.getOrNull(2).orEmpty().trim()
                    if (lieferscheinnummer.isBlank() && existing == null) return@forEach
                    if (palExtra(existing ?: All(), "source") != "system") {
                        val saved = PalettenkontoApi.saveJournal(journalFromRow(id, cols, existing), currentFirmenId())
                        if (saved == null) AppDialogs.alert("Journalbuchung wurde nicht gespeichert: Lieferscheinnummer fehlt oder wurde nicht im WE/WA-Auftrag gefunden.")
                    }
                }
                reload()
            }
        }
    )
}

@Composable
fun PalDifferenzenScreen() {
    val scope = rememberCoroutineScope()
    var rows by remember { mutableStateOf<List<All>>(emptyList()) }
    var letzteFilter by remember { mutableStateOf<Map<String, String>?>(null) }
    val refreshVersion = LocalScreenRefreshVersion.current

    suspend fun reload() {
        rows = letzteFilter?.let { PalettenkontoApi.searchDashboard("differenzen", it) }.orEmpty()
    }

    LaunchedEffect(refreshVersion) { reload() }

    fun uebernehmbarePartnerBuchungen(selected: Set<String>): Set<String> =
        selected.filterTo(mutableSetOf()) { rowId ->
            rows.firstOrNull { it.id == rowId }?.source == "partner"
        }

    TabularScreen(
        screenKey = "PALDifferenzen",
        rollenKey = "PALDifferenzen",
        title = "Palettenkonto Differenzen",
        headers = differenzHeaders,
        models = rows,
        data = rows.map { row -> row.id to differenzRow(row) },
        editable = false,
        hardReadOnly = false,
        nonEditableCols = differenzHeaders.toSet(),
        includeMetadata = true,
        allowCreate = false,
        allowDelete = false,
        allowSave = false,
        allowImportButton = false,
        allowRefreshButton = true,
        allowExportButton = true,
        allowSearchButton = true,
        smartMode = false,
        installButtonLabel = "Buchung übernehmen",
        isInstallEnabled = { selected -> uebernehmbarePartnerBuchungen(selected).isNotEmpty() },
        onInstallSelected = { selected ->
            scope.launch {
                val uebernehmen = uebernehmbarePartnerBuchungen(selected)
                if (uebernehmen.isNotEmpty()) PalettenkontoApi.uebernehmen(uebernehmen, currentFirmenId())
                reload()
            }
        },
        onCancelRows = { selected ->
            scope.launch {
                PalettenkontoApi.stornieren(selected, currentFirmenId())
                reload()
            }
        },
        onRefresh = { scope.launch { reload() } },
        onSearchWithFilters = { filters ->
            scope.launch {
                letzteFilter = filters
                rows = PalettenkontoApi.searchDashboard("differenzen", filters)
            }
        },
        onCommit = {}
    )
}

private fun partnerRow(row: All, saldo: Int): List<String> =
    listOf(
        row.firma,
        row.strasse,
        row.hausnummer,
        row.plz,
        row.ort,
        palExtra(row, "tauschart"),
        palSigned(saldo),
        row.ansprechpartner,
        row.email,
        row.telefon,
        row.mobil,
        palExtra(row, "abschlusstag")
    )

private fun kontoRow(row: All): List<String> =
    listOf(row.bezeichnung, palExtra(row, "eingang"), palExtra(row, "ausgang"), palSigned(palExtra(row, "saldo")))

private fun auskunftRow(row: All, documentsByKey: Map<String, List<All>>): List<String> =
    listOf(
        row.datum,
        palTauschpartner(row),
        row.lieferscheinnummer,
        row.auftraggeber,
        row.empfaenger,
        row.unternehmer,
        row.lieferant,
        palExtra(row, "palettentyp"),
        palVorgangstypLabel(row),
        palExtra(row, "eingang"),
        palExtra(row, "ausgang"),
        palKontobewegungDifferenz(row),
        palSigned(palExtra(row, "saldo")),
        row.lkwkennzeichen,
        row.aufliegerkennzeichen,
        row.fahrername,
        palDocumentIcon(row, documentsByKey, "palettenschein"),
        palDocumentIcon(row, documentsByKey, "prozess"),
        palDocumentIcon(row, documentsByKey, "qualitaet"),
        palDocumentIcon(row, documentsByKey, "lieferschein"),
        palExtra(row, "palSoll"),
        palExtra(row, "palIst"),
        palExtra(row, "grund"),
        ZustandManager.textFor("PAL", row.zustand),
        palExtra(row, "gebuchtVon"),
        palExtra(row, "gebuchtAm"),
        palExtra(row, "gebuchtUm")
    )

private fun journalRow(row: All): List<String> =
    listOf(
        row.datum,
        palTauschpartner(row),
        row.lieferscheinnummer,
        row.auftraggeber,
        row.empfaenger,
        row.unternehmer,
        row.lieferant,
        palExtra(row, "palettentyp"),
        palVorgangstypLabel(row),
        palExtra(row, "eingang"),
        palExtra(row, "ausgang"),
        palExtra(row, "grund"),
        ZustandManager.textFor("PAL", row.zustand)
    )

private fun differenzRow(row: All): List<String> =
    listOf(
        row.datum,
        palTauschpartner(row),
        row.lieferscheinnummer,
        row.auftraggeber,
        row.empfaenger,
        row.unternehmer,
        row.lieferant,
        palExtra(row, "palettentyp"),
        palExtra(row, "vorgangstyp"),
        palExtra(row, "eingang"),
        palExtra(row, "ausgang"),
        palExtra(row, "palSoll"),
        palExtra(row, "palIst"),
        palExtra(row, "grund"),
        ZustandManager.textFor("PAL", row.zustand)
    )

private fun partnerFromRow(id: String, cols: List<String>, existing: All?): All =
    (existing ?: All()).copy(
        id = id,
        firmenId = currentFirmenId(),
        firma = cols.getOrNull(0).orEmpty(),
        kunde = cols.getOrNull(0).orEmpty(),
        strasse = cols.getOrNull(1).orEmpty(),
        hausnummer = cols.getOrNull(2).orEmpty(),
        plz = cols.getOrNull(3).orEmpty(),
        ort = cols.getOrNull(4).orEmpty(),
        ansprechpartner = cols.getOrNull(7).orEmpty(),
        email = cols.getOrNull(8).orEmpty(),
        telefon = cols.getOrNull(9).orEmpty(),
        mobil = cols.getOrNull(10).orEmpty(),
        tauschart = cols.getOrNull(5).orEmpty(),
        abschlusstag = cols.getOrNull(11).orEmpty()
    )

private fun journalFromRow(id: String, cols: List<String>, existing: All?): All {
    val eingang = cols.getOrNull(9).orEmpty().toIntOrNull() ?: 0
    val ausgang = cols.getOrNull(10).orEmpty().toIntOrNull() ?: 0
    val differenz = eingang - ausgang
    val saldo = eingang - ausgang
    val grund = cols.getOrNull(11).orEmpty()
    val datum = cols.getOrNull(0).orEmpty()
    val palettentyp = cols.getOrNull(7).orEmpty()
    val vorgangstyp = palVorgangstypRaw(cols.getOrNull(8).orEmpty())
    return (existing ?: All()).copy(
        id = existing?.id.orEmpty().ifBlank { id },
        firmenId = currentFirmenId(),
        datum = datum,
        kunde = cols.getOrNull(1).orEmpty(),
        firma = cols.getOrNull(1).orEmpty(),
        lieferscheinnummer = cols.getOrNull(2).orEmpty().trim(),
        bezeichnung = palettentyp,
        auftragsart = vorgangstyp,
        palettenanzahl = saldo,
        zustand = existing?.zustand ?: ZustandManager.PAL.IN_KLAERUNG,
        source = "manual",
        kundenId = palExtra(existing ?: All(), "kundenId"),
        tauschpartner = cols.getOrNull(1).orEmpty(),
        palettentyp = palettentyp,
        vorgangstyp = vorgangstyp,
        eingang = eingang.toString(),
        ausgang = ausgang.toString(),
        saldo = saldo.toString(),
        palKontoDifferenz = differenz.toString(),
        grund = grund,
        gebuchtVon = currentUserLabel(),
        gebuchtAm = currentPalDate(),
        gebuchtUm = currentPalTime(),
        monat = currentPalMonthKey()
    )
}

private fun journalRowWithAuftrag(row: List<String>, auftraege: List<All>): List<String> {
    val next = row.toMutableList().apply {
        while (size < journalHeaders.size) add("")
    }
    val ls = next.getOrNull(2).orEmpty().trim()
    val auftrag = auftraege.firstOrNull { it.lieferscheinnummer == ls } ?: return next
    next[0] = next[0].ifBlank { auftrag.datum }
    next[1] = palJournalAuftragTauschpartner(auftrag)
    next[3] = auftrag.auftraggeber
    next[4] = auftrag.empfaenger
    next[5] = auftrag.unternehmer
    next[6] = auftrag.lieferant
    next[7] = palExtra(auftrag, "palettentyp").ifBlank { auftrag.bezeichnung }
    next[8] = palVorgangstypLabel(auftrag)
    return next
}

private fun palExtra(row: All, key: String): String =
    when (key) {
        "source" -> row.source
        "kundenId" -> row.kundenId
        "tauschpartner" -> row.tauschpartner
        "tauschart" -> row.tauschart
        "abschlusstag" -> row.abschlusstag
        "palettentyp" -> row.palettentyp
        "vorgangstyp" -> row.vorgangstyp.ifBlank { row.auftragsart }
        "eingang" -> row.eingang
        "ausgang" -> row.ausgang
        "saldo" -> row.saldo
        "palKontoDifferenz" -> row.palKontoDifferenz
        "palSoll" -> row.palSoll
        "palIst" -> row.palIst
        "grund" -> row.grund
        "gebuchtVon" -> row.gebuchtVon
        "gebuchtAm" -> row.gebuchtAm
        "gebuchtUm" -> row.gebuchtUm
        "monat" -> row.monat
        "palettenart" -> row.palettenart
        "tauschpartnerAdresse" -> row.tauschpartnerAdresse
        "tauschpartnerOrt" -> row.tauschpartnerOrt
        "eingaengeGesamt" -> row.eingaengeGesamt
        "ausgaengeGesamt" -> row.ausgaengeGesamt
        "bewegungenGesamt" -> row.bewegungenGesamt
        "kontostand" -> row.kontostand
        else -> row.palettenWerte[key].orEmpty()
    }

private fun palTauschpartner(row: All): String =
    palExtra(row, "tauschpartner").ifBlank { row.kunde }.ifBlank { row.firma }

private fun palJournalAuftragTauschpartner(auftrag: All): String {
    val vorgangstyp = palExtra(auftrag, "vorgangstyp").ifBlank { auftrag.auftragsart }.uppercase()
    return palExtra(auftrag, "tauschpartner")
        .ifBlank { auftrag.kunde }
        .ifBlank { auftrag.firma }
        .ifBlank { auftrag.unternehmer }
        .ifBlank {
            if (vorgangstyp == "WA") auftrag.empfaenger else auftrag.lieferant
        }
        .ifBlank { auftrag.auftraggeber }
}

private fun palVorgangstypLabel(row: All): String =
    when (palExtra(row, "vorgangstyp").uppercase()) {
        "WE" -> "Liefern"
        "WA" -> "Laden"
        else -> palExtra(row, "vorgangstyp")
    }

private fun palVorgangstypRaw(value: String): String =
    when (value.trim().uppercase()) {
        "LIEFERN" -> "WE"
        "LADEN" -> "WA"
        else -> value.trim().uppercase()
    }

private fun palSigned(value: Int): String =
    if (value >= 0) "+$value" else value.toString()

private fun palSigned(value: String): String =
    palSigned(value.toIntOrNull() ?: 0)

private fun palKontobewegungDifferenz(row: All): String {
    palExtra(row, "palKontoDifferenz").takeIf { it.isNotBlank() }?.let { return palSigned(it) }
    val eingang = palExtra(row, "eingang").toIntOrNull() ?: 0
    val ausgang = palExtra(row, "ausgang").toIntOrNull() ?: 0
    return palSigned(eingang - ausgang)
}

private fun palettenkontoabschlussSnapshot(snapshot: All, partner: All?): All {
    val kundenId = palExtra(snapshot, "kundenId").ifBlank { partner?.id.orEmpty() }
    val monthKey = palExtra(snapshot, "monat").ifBlank { snapshot.datum.ifBlank { currentPalMonthKey() } }
    val partnerFirma = palExtra(snapshot, "tauschpartner").ifBlank { partner?.firma.orEmpty() }.ifBlank { snapshot.kunde }
    val palettenart = palExtra(snapshot, "palettenart")
    return All(
        id = "palettenkontoabschluss-$monthKey-$kundenId-$palettenart",
        datum = currentPalDate(),
        abschlussMonat = monthKey,
        abschlussMonatLabel = palMonthLabelFromKey(monthKey),
        kundenId = kundenId,
        tauschpartner = partnerFirma,
        tauschpartnerAdresse = palExtra(snapshot, "tauschpartnerAdresse"),
        tauschpartnerOrt = palExtra(snapshot, "tauschpartnerOrt"),
        eingaengeGesamt = palExtra(snapshot, "eingaengeGesamt"),
        ausgaengeGesamt = palExtra(snapshot, "ausgaengeGesamt"),
        bewegungenGesamt = palExtra(snapshot, "bewegungenGesamt"),
        kontostand = palSigned(palExtra(snapshot, "kontostand")),
        palettenart = palettenart
    )
}

private fun palettenkontoabschlussPdfFileName(abschluss: All): String {
    val month = abschluss.abschlussMonat.ifBlank { currentPalMonthKey() }
    val partner = palFileSegment(abschluss.kundenId.ifBlank { abschluss.tauschpartner })
    val palettenart = palFileSegment(abschluss.palettenart)
    return "palettenabschluss-${currentFirmenId()}-$partner-$palettenart-$month.pdf"
}

private fun palFileSegment(value: String): String =
    value.trim().lowercase().replace(Regex("[^a-z0-9äöüß_-]+"), "-").trim('-').ifBlank { "paletten" }

private fun palettenkontoabschlussLhmBezeichnung(value: String): String =
    value.trim()
        .replace(Regex("\\s+Paletten?$", RegexOption.IGNORE_CASE), "")
        .replace(Regex("\\s+Palette$", RegexOption.IGNORE_CASE), "")
        .trim()

private fun palMonthLabelFromKey(monthKey: String): String {
    val year = monthKey.substringBefore("_").toIntOrNull()
    val month = monthKey.substringAfter("_", "").toIntOrNull()
    if (year == null || month == null) return currentPalMonthLabel()
    return "${palMonthName(month)} $year"
}

private fun palMonthName(month: Int): String =
    when (month) {
        1 -> "Januar"
        2 -> "Februar"
        3 -> "März"
        4 -> "April"
        5 -> "Mai"
        6 -> "Juni"
        7 -> "Juli"
        8 -> "August"
        9 -> "September"
        10 -> "Oktober"
        11 -> "November"
        12 -> "Dezember"
        else -> month.toString()
    }

private suspend fun palLoadDocuments(rows: List<All>): Map<String, List<All>> {
    val result = mutableMapOf<String, List<All>>()
    rows
        .filter { it.lieferscheinnummer.isNotBlank() }
        .map { palDocumentListKey(it) to it }
        .distinctBy { it.first }
        .forEach { (key, row) ->
            result[key] = runCatching { ServiceApi.serviceDocuments(palServiceType(row), row.lieferscheinnummer) }.getOrDefault(emptyList())
        }
    return result
}

private fun palAuskunftDocumentKind(colIndex: Int): String =
    when (auskunftHeaders.getOrNull(colIndex)) {
        "Palettenschein" -> "palettenschein"
        "Auftragsdokumentationen" -> "prozess"
        "QM Dokumentation" -> "qualitaet"
        "Lieferscheine" -> "lieferschein"
        else -> ""
    }

private fun palDocumentIcon(row: All, documentsByKey: Map<String, List<All>>, kind: String): String =
    if (palDocument(row, documentsByKey, kind) != null) TABULAR_DOCUMENT_ICON_VALUE else ""

private suspend fun palOpenDocument(rows: List<All>, documentsByKey: Map<String, List<All>>, rowId: String, kind: String) {
    val row = rows.firstOrNull { it.id == rowId } ?: return
    val doc = palDocument(row, documentsByKey, kind) ?: return
    ServiceApi.openServiceDocumentMeta(palServiceType(row), row.lieferscheinnummer, doc)
}

private fun palDocument(row: All, documentsByKey: Map<String, List<All>>, kind: String): All? {
    val expected = palExpectedFileName(row, kind).lowercase()
    if (expected.isBlank()) return null
    return documentsByKey[palDocumentListKey(row)]
        .orEmpty()
        .firstOrNull { doc ->
            val file = doc.file.ifBlank { "${doc.name}.pdf" }.lowercase()
            file == expected || file.substringBeforeLast(".") == expected.substringBeforeLast(".")
        }
        ?: documentsByKey[palDocumentListKey(row)]
            .orEmpty()
            .firstOrNull { doc ->
                val file = doc.file.ifBlank { "${doc.name}.pdf" }.lowercase()
                val prefix = "${row.lieferscheinnummer}-${palLkwDocumentId(row)}".lowercase()
                file.startsWith(prefix) && when (kind) {
                    "palettenschein" -> file.contains("palettenschein")
                    "prozess" -> file.contains("prozessdokumentation")
                    "qualitaet" -> file.contains("qualitaetsdokumentation") || file.contains("qualitätsdokumentation")
                    "lieferschein" -> file.contains("lieferschein")
                    else -> false
                }
            }
}

private fun palExpectedFileName(row: All, kind: String): String {
    val ls = row.lieferscheinnummer
    val lkw = palLkwDocumentId(row)
    if (ls.isBlank() || lkw.isBlank()) return ""
    val standort = row.originalStandortKey
        .ifBlank { row.waStandortId }
        .ifBlank { row.slotStandortId }
        .ifBlank { row.slotYardStandortKey }
        .takeIf { it.isNotBlank() }
        ?.let { "-standort-${it.filter { ch -> ch.isLetterOrDigit() || ch == '-' || ch == '_' }}" }
        .orEmpty()
    return when (kind) {
        "palettenschein" -> "${ls}-${lkw}${standort}_palettenschein.pdf"
        "prozess" -> "${ls}-${lkw}${standort}_prozessdokumentation.pdf"
        "qualitaet" -> "${ls}_${lkw}${standort.replace("-", "_")}_qualitaetsdokumentation.pdf"
        "lieferschein" -> "${ls}-${lkw}_lieferschein.pdf"
        else -> ""
    }
}

private fun palLkwDocumentId(row: All): String =
    row.originalLfdNr
        .takeIf { it.toIntOrNull()?.let { number -> number > 0 } == true }
        ?: row.originalId

private fun palDocumentListKey(row: All): String =
    "${palServiceType(row)}:${row.lieferscheinnummer}"

private fun palServiceType(row: All): String =
    if (palExtra(row, "vorgangstyp").uppercase() == "WA") "yard-wa" else "yard-we"
